import './FamilyPages.css';
import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, Sparkles, Film, Trophy, Baby, CalendarDays } from 'lucide-react';
import SEO from '../components/SEO';
import MediaTruthSurface from '../components/MediaTruthSurface';
import { ServiceBreadcrumb, ServiceFacts } from '../components/ServiceHeader';
import { MEDIA_SLOTS } from '../lib/mediaTruth';

const clubFeatures = [
  { icon: Film, title: 'Movie Room', text: 'Our theater is the perfect place for kids to wind down with their favorite approved shows and movies!' },
  { icon: Trophy, title: 'Air Hockey Table', text: 'Experience the thrill of friendly competition at our air hockey table. This is the perfect chance for kids to learn, have fun, and make lasting friendships!' },
  { icon: ShieldCheck, title: 'Safety', text: 'All Kid’s Club employees undergo background checks, hold CPR certification, and maintain a low ratio of children per childcare staff member. The Kid’s Club is fully gated for staff and children, and only individuals on the authorized pick-up list can retrieve a child.' },
  { icon: Baby, title: 'Indoor Jungle Gym', text: 'Our two-story indoor jungle gym gives children a safe place to run, climb, play, and enjoy an active adventure.' },
  { icon: Trophy, title: 'Mini Basketball Court', text: 'Our mini basketball court is where young talents can take their first shots. This court puts the fun in playtime!' },
  { icon: Sparkles, title: 'Sanitation', text: 'Our dedicated sanitization staff works around the clock to keep every item spotless, giving children a safe, clean environment for supervised play.' },
];

const events = [
  ['Pool Parties', 'Drop your kids off at our youth pool parties—kids swim, enjoy pizza, watch movies, and make friends while you enjoy some well-deserved time off.'],
  ['Birthday Parties', 'Request a two-hour Kids Zone Party or Kids Zone & Beach Party for up to 20 children.', '/childcare/birthday-party'],
  ['Kids Camps', 'Elevate your child’s summer with outdoor water games, rock climbing, kickboxing, yoga, crafts, and more while they create unforgettable memories.'],
  ['Holiday Events', 'Enjoy community favorites including our free Trunk or Treat extravaganza in October with 30+ vendors, food trucks, photo booths, jump houses, car shows, and a youth Christmas event in winter.'],
  ["Parent's Night Out", 'Parents can take a breather while kids enjoy crafts, games, pizza, and more.'],
  ['Water Safety Camps', 'Equip your children with essential water-safety skills for summer adventures and greater safety around water.'],
];

const hours = [
  ['Monday–Thursday', '8:00am–8:00pm'],
  ['Friday–Sunday', '8:00am–1:00pm'],
];

const KidsFamilyPage = () => (
  <div className="dv-family dv-kids-family" data-testid="kids-family-page">
    <SEO
      title="Kids & Family Programs"
      description="Explore Delta Valley's Kids Club, childcare hours, family events, camps, and Moms in Motion classes in Brentwood."
      keywords="gym childcare Brentwood, Kids Club, family gym, kids camps, Moms in Motion"
    />

    <section className="dv-family__hero">
      <div className="dv-family__hero-copy">
        <div className="dv-service-panel">
          <ServiceBreadcrumb current="Kids & Family" />
          <h1 className="dv-size-heading dv-service-title mb-6">Families Thrive Here</h1>
          <p className="dv-size-lede text-xl text-blue-100">
            Explore our impeccably clean Kids Center where endless fun awaits your little
            ones. From a movie room and mini basketball court to air hockey tables,
            endless toys, slides, and obstacle courses, our facility is designed for
            children of all ages. Delta Valley even has a dedicated room for the youngest
            members aged 6 months to 2 years!
          </p>
          <div className="dv-service-actions">
            <Link className="dv-button dv-button--light" to={publicHref('/childcare/birthday-party')}>Birthday Parties</Link>
            <Link className="dv-button dv-button--outline-light" to={publicHref('/book-a-tour')}>Book a Tour</Link>
          </div>
        </div>
      </div>
      <MediaTruthSurface media={MEDIA_SLOTS.kidsFamilyHero} className="dv-family__hero-media" decorative />
      <ServiceFacts items={["Movie room & mini basketball court", "Slides, toys & obstacle courses", "A room for ages 6 months to 2 years"]} />
    </section>

    <section className="py-16">
      <div className="container-custom">
        <h2 className="dv-size-heading mb-10 text-3xl font-bold">Kid&apos;s Club</h2>
        <div className="dv-family__feature-grid">
          {clubFeatures.map(({ icon: Icon, title, text }) => (
            <Card key={title} className="dv-family__panel">
              <CardContent className="p-6">
                <Icon className="text-[#0072BB] mb-4" size={30} />
                <h3 className="dv-size-subheading text-xl font-bold mb-2">{title}</h3>
                <p className="dv-size-body text-gray-600 leading-relaxed">{text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-gray-50">
      <div className="container-custom grid lg:grid-cols-2 gap-8">
        <Card className="dv-family__panel">
          <CardContent className="p-7">
            <div className="flex items-center gap-3 mb-5">
              <CalendarDays className="text-[#0072BB]" />
              <h2 className="dv-size-subheading text-2xl font-bold">Kids Club Hours</h2>
            </div>
            <div className="divide-y">
              {hours.map(([day, time]) => (
                <div key={day} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="font-semibold">{day}</span>
                  <span className="text-gray-600">{time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="dv-family__panel">
          <CardContent className="p-7">
            <h2 className="dv-size-subheading text-2xl font-bold mb-4">Moms in Motion</h2>
            <p className="dv-size-body text-gray-700 leading-relaxed mb-4">
              Moms in Motion (MIM) is designed for parents with little ones who are too
              young for our childcare program. Bring your baby in a stroller or car seat
              to regular Group X classes marked with an asterisk. Have peace of mind while
              you work out and remain able to tend to your baby&apos;s needs. Check the
              schedule for qualifying classes.
            </p>
            <Link to={publicHref("/schedule")} className="text-[#0072BB] font-semibold hover:underline">View qualifying classes →</Link>
          </CardContent>
        </Card>
      </div>
    </section>

    <section className="py-16">
      <div className="container-custom">
        <h2 className="dv-size-heading text-3xl font-bold mb-10">Kids Events</h2>
        <div className="dv-family__event-grid">
          {events.map(([title, text, path]) => (
            <Card key={title} className="dv-family__panel">
              <CardContent className="p-6">
                <h3 className="dv-size-subheading font-bold text-lg mb-2">{title}</h3>
                <p className="dv-size-body text-gray-600">{text}</p>
                {path && <Link to={publicHref(path)} className="mt-4 inline-flex font-semibold text-[#0072BB] hover:underline">Request a Birthday Party →</Link>}
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-8"><Link to={publicHref("/blog")}><Button variant="outline">See Upcoming Kids Events</Button></Link></div>
      </div>
    </section>

    <section className="py-16 bg-[#0072BB] text-white">
      <div className="container-custom text-center">
        <h2 className="dv-size-heading text-3xl md:text-4xl font-bold mb-4">Put Your Family&apos;s Health First</h2>
        <p className="dv-size-lede text-blue-100 text-lg mb-8">
          Unlock youth event discounts as an active club member! Lead by example for
          your kids by showing them the path to health and wellness!
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Link to={publicHref("/membership")}><Button size="lg" variant="secondary">View Memberships</Button></Link>
          <Link to={publicHref("/book-a-tour")}><Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0072BB]">Book a Tour</Button></Link>
        </div>
      </div>
    </section>
  </div>
);

export default KidsFamilyPage;
